import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { gettingPaycodeDistribution } from 'utils/PaycodeDistribution';
import { ListItemIcon, ListItemText, Menu, MenuItem /* Snackbar, Alert */ } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import { Add } from 'components/dialog/paycodeDistribution/Add';
import { Delete } from 'components/dialog/paycodeDistribution/Delete';
import { Edit } from 'components/dialog/paycodeDistribution/Edit';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, reloading, rows }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openDelete, setOpenDelete] = useState(false);
    const basePath = buildSettingsBasePath('paycode_distribution');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'paycode_distribution'), [location.pathname]);
    const openAdd = modalRoute.action === 'add';
    const openEdit = (modalRoute.action === 'update' || modalRoute.action === 'detail') && modalRoute.id !== null;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeRouteModal = () => {
        navigate(basePath);
    };
    /* const [openAlert, setOpenAlert] = useState(false) */
    /* const [alert, setAlert] = useState({msg: '', severity: ''}) */

    const options = [
        {
            name: t('settings.paycode_distribution.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('paycode_distribution', 'add'));
                setContextMenu(null);
            },
            color: '',
            selected: false,
            onlyOne: false,
            permission: JSON.parse(localStorage.getItem('permissions')).find((permision) => permision === 'timekeeper.CreatePaycodesDistribution') ? true : false,
        },
        {
            name: t('settings.paycode_distribution.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('paycode_distribution', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permission: JSON.parse(localStorage.getItem('permissions')).find((permision) => permision === 'timekeeper.UpdatePaycodesDistribution') ? true : false,
        },
        {
            name: t('settings.paycode_distribution.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setOpenDelete(true);
                setContextMenu(null);
            },
            color: 'error',
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: false,
            onlyOne: true,
            permission: JSON.parse(localStorage.getItem('permissions')).find((permision) => permision === 'timekeeper.DeletePaycodesDistribution') ? true : false,
        },
        {
            name: t('settings.paycode_distribution.table.context_menu.reload'),
            icon: <CachedIcon />,
            color: '',
            click: () => {
                reloading();
                setContextMenu(null);
            },
            permission: true,
        },
    ];

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((option) => option.permission)
                    .map((option, index) => (
                        <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>

                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    ))}
            </Menu>

            <Add open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} reloading={reloading} rows={rows} />
            <Edit open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} selectedRow={selectedRowsForRoute} reloading={reloading} rows={rows} />
            <Delete open={openDelete} setOpen={setOpenDelete} selectedRow={selectedRow} reloading={reloading} />

            {/* <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={openAlert}
            >
                <Alert
                    onClose={() => setOpenAlert(false)}
                    severity= {alert.severity}
                    sx={{ width: '100%' }}
                >
                    {alert.msg}
                </Alert>
            </Snackbar> */}
        </>
    );
};

const PaycodeDistributionTable = () => {
    const { t } = useTranslation();

    const [loadingPaycodeDistribution, setLoadingPaycodeDistribution] = useState(true);
    const [rows, setRows] = useState([]);
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedRow, setSelectedRow] = useState([]);

    const columns = [
        {
            field: 'name',
            headerName: t('settings.paycode_distribution.table.columns.name'),
            flex: 1,
        },
        {
            field: 'description',
            headerName: t('settings.paycode_distribution.table.columns.description'),
            flex: 1,
            filterable: false,
        },
        {
            field: 'combination_rule',
            headerName: t('settings.paycode_distribution.table.columns.combination_rule'),
            flex: 1,
            filterable: false,
        },
    ];

    const reloading = () => {
        /* if(localStorage.getItem('access_token') && JSON.parse(localStorage.getItem('permissions')).find(permision => permision === 'timekeeper.GetPaycodesDistribution')) {
            gettingPaycodeDistribution(setLoadingPaycodeDistribution, setRows)
        }else{
            setRows([])
            setLoadingPaycodeDistribution(false)
        } */
        gettingPaycodeDistribution(setLoadingPaycodeDistribution, setRows);
    };

    useEffect(() => {
        reloading(setLoadingPaycodeDistribution, setRows);
    }, []);

    return (
        <>
            <div
                style={{ width: '100%', height: '100%' }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
                }}
            >
                <DataGridPro
                    columns={columns}
                    rows={rows}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    headerFilters
                    localeText={{
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1
                                ? t('general.table.footerFilters.footerRowSelected.>1', { count: count.toLocaleString() })
                                : t('general.table.footerFilters.footerRowSelected.=1', { count: count.toLocaleString() }),
                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                t('general.table.footerFilters.labelDisplayedRows', { from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString() }),
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }}
                    disableColumnMenu
                    loading={loadingPaycodeDistribution}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        setSelectedRow(newRowSelectionModel);
                    }}
                    rowSelectionModel={selectedRow}
                    pagination
                    sx={{
                        height: 'calc(100vh - 140px)',
                    }}
                />

                <ContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    selectedRow={rows.filter((row) => selectedRow.includes(row.id)).map((row) => ({ id: row.id, name: row.name }))}
                    reloading={reloading}
                    rows={rows}
                />
            </div>
        </>
    );
};

export { PaycodeDistributionTable };
